/**
 * How It Works Section - Clean step cards with visuals
 */

import { ClipboardList, Bell, DollarSign, ArrowRight, FileUp, Tags } from 'lucide-react';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import EditableElement from '../admin/EditableElement';

const steps = [
  {
    id: 1,
    icon: ClipboardList,
    title: 'Create a Checklist',
    description: 'Start a project, upload your IKEA Planner design, or add items while browsing with our extension.',
    visual: 'search',
  },
  {
    id: 2,
    icon: Bell,
    title: 'We Watch for Deals',
    description: 'We monitor As-Is, Last Chance, Family Offers, and Limited Time sales 24/7 across all your items.',
    visual: 'alert',
  },
  {
    id: 3,
    icon: DollarSign,
    title: 'Get Notified, Save Big',
    description: 'When your items hit As-Is or go on sale, you get an alert. Buy at 50-70% off retail.',
    visual: 'save',
  },
];

interface VisualProps {
  t: (key: string, fallback: string) => string;
}

function SearchVisual({ t }: VisualProps) {
  return (
    <div className="mt-6 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">{t('howItWorks.visual.kitchenRenovation', 'Kitchen Renovation')}</span>
        <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">{t('howItWorks.visual.items', '12 items')}</span>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 p-2 bg-primary-50 rounded-lg border border-primary-100">
          <div className="w-6 h-6 bg-primary-600 rounded flex items-center justify-center">
            <FileUp className="w-3 h-3 text-white" />
          </div>
          <span className="text-sm text-gray-600">{t('howItWorks.visual.sektionCabinet', 'SEKTION base cabinet')}</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
          <div className="w-6 h-6 bg-gray-200 rounded" />
          <span className="text-sm text-gray-600">{t('howItWorks.visual.maximeraDrawer', 'MAXIMERA drawer')}</span>
        </div>
      </div>
    </div>
  );
}

function AlertVisual({ t }: VisualProps) {
  return (
    <div className="mt-6 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">{t('howItWorks.visual.watching', 'Watching')}</span>
        <div className="flex items-center gap-1">
          <Tags className="w-3 h-3 text-primary-600" />
          <span className="text-xs text-primary-600">{t('howItWorks.visual.allSales', 'All sales')}</span>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded-lg">
          <span className="text-xs font-medium text-yellow-700">{t('howItWorks.visual.asIsInStore', 'As-Is In-Store')}</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
          <span className="text-xs font-medium text-blue-700">{t('howItWorks.visual.lastChance', 'Last Chance')}</span>
        </div>
        <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
          <span className="text-xs font-medium text-purple-700">{t('howItWorks.visual.familyOffers', 'Family Offers')}</span>
        </div>
      </div>
    </div>
  );
}

function SaveVisual({ t }: VisualProps) {
  return (
    <div className="mt-6 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100 mb-3">
        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
          <Bell className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('howItWorks.visual.asIsAlert', 'As-Is Alert!')}</p>
          <p className="text-xs text-green-600">{t('howItWorks.visual.sektionAtStore', 'SEKTION at your store')}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <span className="text-2xl font-bold text-green-600">$89</span>
          <span className="text-sm text-gray-400 line-through ml-2">$249</span>
        </div>
        <div className="px-3 py-1 bg-accent text-white text-sm font-semibold rounded-lg">
          {t('howItWorks.visual.percentOff', '64% off')}
        </div>
      </div>
    </div>
  );
}

const visuals: Record<string, React.ComponentType<VisualProps>> = {
  search: SearchVisual,
  alert: AlertVisual,
  save: SaveVisual,
};

export default function HowItWorks() {
  const { t } = useLanguage();
  const content = useWebsiteContentOptional();

  const get = (key: string, fallback: string) =>
    content?.getContent('how_it_works', key, fallback) ?? fallback;

  return (
    <section className="py-20 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <EditableElement section="how_it_works" field="badge">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 rounded-full text-sm font-medium text-primary-700 mb-4">
              {get('badge', 'How It Works')}
            </div>
          </EditableElement>
          <EditableElement section="how_it_works" field="title">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              {get('title', 'Start saving in')}
              <span className="text-primary-600">
                {' '}{get('title_highlight', '3 easy steps')}
              </span>
            </h2>
          </EditableElement>
          <EditableElement section="how_it_works" field="subtitle">
            <p className="mt-4 text-lg text-gray-600">
              {get('subtitle', 'Getting started takes less than a minute. No credit card required.')}
            </p>
          </EditableElement>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const Visual = visuals[step.visual];
            const title = get(`step${step.id}_title`, step.title);
            const description = get(`step${step.id}_desc`, step.description);

            return (
              <EditableElement key={step.id} section="how_it_works" field={`step${step.id}_title`}>
                <div className="relative">
                  {/* Connector arrow (hidden on mobile) */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:flex absolute top-16 -right-6 lg:-right-8 z-10">
                      <div className="w-12 lg:w-16 flex items-center justify-center">
                        <ArrowRight className="w-6 h-6 text-gray-300" />
                      </div>
                    </div>
                  )}

                  {/* Step card */}
                  <div className="relative p-6 rounded-2xl bg-gray-50 border border-gray-200 hover:border-primary-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    {/* Step number */}
                    <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                      {index + 1}
                    </div>

                    {/* Icon */}
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 bg-primary-600 text-white shadow-md">
                      <Icon className="w-7 h-7" />
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-600 leading-relaxed">{description}</p>

                    {/* Visual mockup */}
                    <Visual t={t} />
                  </div>
                </div>
              </EditableElement>
            );
          })}
        </div>
      </div>
    </section>
  );
}
